import { MOTZU_STORE_CATEGORY_SLUGS } from '../../constants/motzuStoreCategorySlugs';
import type {
  SpecCatalogEntry,
  StoreLayoutSettings,
  StoreMetafieldDefinitionOption,
} from '../../services/integrationApi';

export type ProductTypeRule = NonNullable<StoreLayoutSettings['productTypeRules']>[number];
export type FallbackMode = 'derive' | 'default' | 'flag';
export type ResolutionStep = 'category' | 'rule' | 'fallback';

/** Same shape the old "custom slug" box produced. */
export const normalizeMotzuSlug = (raw: string): string =>
  raw.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

export const titleCaseSlug = (slug: string): string =>
  slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Every Motzu category this integration can map. Extra slugs the merchant
 * just added stay at the front; the rest are alphabetical. `additional` adds
 * rows a flag turns on (the lens focal-range keys) without saving them.
 */
export function categorySlugs(
  layout: StoreLayoutSettings | undefined,
  extra: string[] = [],
  additional: readonly string[] = [],
): string[] {
  const map = layout?.categoryMap || {};
  const base = layout?.allMotzuCategorySlugs?.length ? layout.allMotzuCategorySlugs : MOTZU_STORE_CATEGORY_SLUGS;
  const extraSet = new Set(extra);
  const rest = Array.from(new Set([...base, ...additional, ...Object.keys(map)])).filter((slug) => !extraSet.has(slug));
  rest.sort((a, b) => a.localeCompare(b));
  return [...extra, ...rest];
}

/** Flag wins when an older save set both a default type and the flag. */
export function fallbackModeOf(layout: StoreLayoutSettings | undefined): FallbackMode {
  if (layout?.flagUnmappedCategories) return 'flag';
  if (layout?.defaultProductType?.trim()) return 'default';
  return 'derive';
}

/** The three fallbacks are exclusive: saving one clears the others. */
export function withFallbackMode(
  layout: StoreLayoutSettings,
  mode: FallbackMode,
  defaultProductType?: string,
): StoreLayoutSettings {
  if (mode === 'flag') {
    return { ...layout, flagUnmappedCategories: true, defaultProductType: undefined };
  }
  if (mode === 'default') {
    const next = (defaultProductType ?? layout.defaultProductType ?? '').trim();
    return { ...layout, flagUnmappedCategories: false, defaultProductType: next || undefined };
  }
  return { ...layout, flagUnmappedCategories: false, defaultProductType: undefined };
}

export function fallbackLabel(slug: string, mode: FallbackMode, defaultProductType?: string): string {
  if (mode === 'flag') return `[UNMAPPED] ${slug || 'Unknown'}`;
  if (mode === 'default' && defaultProductType?.trim()) return defaultProductType.trim();
  if (slug) return titleCaseSlug(slug);
  return 'Camera';
}

/**
 * What the next sync would write as product_type.
 * Order matches `ShopifyService.buildShopifyTaxonomy`: the category map,
 * then a name rule, then the fallback. Name rules do not outrank a mapped category.
 */
export function resolveStoreProductType(input: {
  name: string;
  category: string;
  enabled: boolean;
  categoryMap: Record<string, string>;
  rules: ProductTypeRule[];
  mode: FallbackMode;
  defaultProductType?: string;
}): { type: string; step: ResolutionStep; ruleKeyword?: string } {
  const category = input.category.trim();
  if (input.enabled) {
    const mapped = input.categoryMap[category] || input.categoryMap[category.toLowerCase()];
    if (mapped?.trim()) return { type: mapped.trim(), step: 'category' };
  }
  const probe = input.name.toLowerCase();
  for (const rule of input.rules) {
    const keyword = rule.keywords.find((kw) => kw.trim() && probe.includes(kw.trim().toLowerCase()));
    if (keyword && rule.productType.trim()) {
      return { type: rule.productType.trim(), step: 'rule', ruleKeyword: keyword.trim() };
    }
  }
  return { type: fallbackLabel(category, input.mode, input.defaultProductType), step: 'fallback' };
}

/** Drop blank rules. An empty keyword matches every product name on the server. */
export function rulesForSave(rules: ProductTypeRule[] | undefined): ProductTypeRule[] {
  return (rules || [])
    .map((rule) => ({
      keywords: rule.keywords.map((kw) => kw.trim()).filter(Boolean),
      productType: rule.productType.trim(),
      ...(rule.taxonomyCategoryGid?.trim() ? { taxonomyCategoryGid: rule.taxonomyCategoryGid.trim() } : {}),
    }))
    .filter((rule) => rule.keywords.length > 0 && rule.productType);
}

export function motzuKeySet(fields: Array<{ namespace: string; key: string }>): Set<string> {
  return new Set(fields.map((field) => `${field.namespace}.${field.key}`));
}

export function visibleStoreDefinitions<T extends { namespace: string; key: string }>(
  definitions: T[],
  motzuKeys: Set<string>,
): T[] {
  return definitions.filter((definition) => !motzuKeys.has(`${definition.namespace}.${definition.key}`));
}

/** Pure numbers, colour codes, and labels used by a single product stay hidden until a search. */
export function isNoisySpecLabel(label: string, productCount: number, hasCounts: boolean): boolean {
  const text = label.trim();
  if (/^\d+(?:\.\d+)?$/.test(text)) return true;
  if (/^#?[0-9a-f]{6}$/i.test(text)) return true;
  if (/^[0-9a-f]{6}\b/i.test(text)) return true;
  if (hasCounts && productCount <= 1) return true;
  return false;
}

const SPEC_SYNONYMS: Record<string, string> = {
  'weather sealed': 'weather sealing',
  'weather sealing': 'weather sealed',
  'max video resolution': 'video resolution',
  'video resolution': 'max video resolution',
};

export function normalizeSpecName(value: string): string {
  return value.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function suggestSpecLabel(
  definition: { name: string; key: string },
  catalog: SpecCatalogEntry[],
): string | null {
  const name = normalizeSpecName(definition.name);
  const key = normalizeSpecName(definition.key);
  const targets = new Set([name, key, SPEC_SYNONYMS[name], SPEC_SYNONYMS[key]].filter((value): value is string => Boolean(value)));
  const hit = catalog.find((entry) => targets.has(normalizeSpecName(entry.label)));
  return hit?.label ?? null;
}

/** Definition types the sync can fill. Mirrors `SPEC_METAFIELD_WRITABLE_TYPES` on the server. */
export const WRITABLE_DEFINITION_TYPES: readonly string[] = [
  'single_line_text_field',
  'multi_line_text_field',
  'number_integer',
  'number_decimal',
  'boolean',
  'list.single_line_text_field',
];

export const isWritableDefinitionType = (type: string): boolean => WRITABLE_DEFINITION_TYPES.includes(type);

/**
 * Same conversion as `valueForDefinitionType` in `src/utils/shopifySpecMetafields.ts`.
 * A value that returns null here is skipped by the sync, so the preview must say so.
 */
export function convertSpecValue(raw: string, definitionType: string): string | null {
  const text = raw.trim();
  if (!text) return null;
  if (definitionType === 'single_line_text_field') return raw.replace(/\s*\n+\s*/g, ' ').trim() || null;
  if (definitionType === 'multi_line_text_field') return raw;
  if (definitionType === 'number_integer') {
    const cleaned = raw.replace(/,/g, '').trim();
    return /^-?\d+$/.test(cleaned) ? String(parseInt(cleaned, 10)) : null;
  }
  if (definitionType === 'number_decimal') {
    const cleaned = raw.replace(/,/g, '').trim();
    if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
    const value = Number(cleaned);
    return Number.isFinite(value) ? String(value) : null;
  }
  if (definitionType === 'boolean') {
    if (/^(true|yes)$/i.test(text)) return 'true';
    if (/^(false|no)$/i.test(text)) return 'false';
    return null;
  }
  if (definitionType === 'list.single_line_text_field') {
    const parts = raw.split(/\s*[|,;/]\s*|\n+/).map((part) => part.trim()).filter(Boolean);
    return parts.length > 0 ? JSON.stringify(parts) : null;
  }
  return null;
}

/** Fit hint for a spec against a definition, judged by what the sync would actually write. */
export function specFit(
  entry: Pick<SpecCatalogEntry, 'kind' | 'sample'>,
  definitionType: string,
): { text: string; ok: boolean } {
  if (!isWritableDefinitionType(definitionType)) return { text: 'Type not supported', ok: false };
  if (definitionType === 'boolean') {
    return convertSpecValue(entry.sample, definitionType) !== null ? { text: 'Fits', ok: true } : { text: 'Needs Yes/No', ok: false };
  }
  if (definitionType === 'number_decimal') {
    return convertSpecValue(entry.sample, definitionType) !== null ? { text: 'Fits', ok: true } : { text: 'Needs a plain number', ok: false };
  }
  if (definitionType === 'number_integer') {
    return convertSpecValue(entry.sample, definitionType) !== null ? { text: 'Fits', ok: true } : { text: 'Needs a whole number', ok: false };
  }
  return { text: 'Fits', ok: true };
}

export function previewSpecWrite(
  sample: string,
  definitionType: string,
): { value: string; note: string; lossy: boolean } | null {
  if (!sample.trim()) return null;
  if (!isWritableDefinitionType(definitionType)) {
    return { value: '—', note: 'The sync cannot fill this metafield type', lossy: true };
  }
  const value = convertSpecValue(sample, definitionType);
  if (definitionType === 'boolean') {
    return value !== null
      ? { value, note: 'Written as true or false', lossy: false }
      : { value: '—', note: 'Only Yes/No values are written; others are skipped', lossy: true };
  }
  if (definitionType === 'number_decimal' || definitionType === 'number_integer') {
    return value !== null
      ? { value, note: 'Written as a number', lossy: false }
      : { value: '—', note: 'Values with units or text are skipped', lossy: true };
  }
  if (definitionType === 'list.single_line_text_field' && value) {
    const parts = JSON.parse(value) as string[];
    return { value: parts.join(' · '), note: `Written as a list of ${parts.length}`, lossy: false };
  }
  return value !== null ? { value, note: '', lossy: false } : null;
}

export function specFillCount(
  definitions: StoreMetafieldDefinitionOption[],
  motzuKeys: Set<string>,
  rows: Array<{ namespace: string; key: string; specLabel: string }>,
): { filled: number; total: number } {
  const visible = visibleStoreDefinitions(definitions, motzuKeys);
  const filled = visible.filter((definition) =>
    rows.some(
      (row) => row.namespace === definition.namespace && row.key === definition.key && row.specLabel.trim(),
    ),
  ).length;
  return { filled, total: visible.length };
}

const METAFIELD_TYPE_LABEL: Record<string, string> = {
  single_line_text_field: 'Text',
  multi_line_text_field: 'Long text',
  number_decimal: 'Decimal',
  number_integer: 'Integer',
  boolean: 'True / false',
  weight: 'Weight',
  json: 'JSON',
  'list.single_line_text_field': 'List',
};

export function metafieldTypeLabel(type: string): string {
  return METAFIELD_TYPE_LABEL[type] || type;
}

/** The last part of a taxonomy label ("Camera Bags & Cases"), for tight table cells. */
export function taxonomyShortLabel(label: string): string {
  const parts = label.split('>').map((part) => part.trim());
  return parts[parts.length - 1] || label;
}

/**
 * Stored in `unmappedTaxonomyCategory` in place of a GID: "Don't set" (flag
 * `shopify_unmapped_category_unset`). Mirrors the server's `UNMAPPED_TAXONOMY_CATEGORY_UNSET`.
 */
export const UNMAPPED_CATEGORY_UNSET = 'unset';

/** The store's fallback category GID, or undefined when it chose none or chose "Don't set". */
export function unmappedFallbackGid(layout: StoreLayoutSettings | undefined): string | undefined {
  const value = layout?.unmappedTaxonomyCategory;
  return value && value !== UNMAPPED_CATEGORY_UNSET ? value : undefined;
}

/** The "Don't set" option's label, shared by the v2 FallbackStep and the legacy mapping section. */
export const UNMAPPED_CATEGORY_UNSET_LABEL = "Don't set (leave Shopify's category as is)";

/** The same choice where a category map row shows what it falls back to (row default, tight cells). */
export const UNMAPPED_CATEGORY_UNSET_SHORT = "Don't set (leave as is)";

/**
 * The note under the unmatched-category select: what the current choice does to a listing's category.
 * `allowUnset` is flag `shopify_unmapped_category_unset`; `unset` is "Don't set" being the current choice.
 */
export function unmappedCategoryNote(allowUnset: boolean, unset: boolean): string {
  if (!allowUnset) return 'Motzu always sets a Shopify Product Category, so Shopify never guesses one from the title.';
  if (unset) {
    return "Motzu leaves Shopify's category as it is: a new product is created with no category, and a listing that already has one keeps it. Motzu never clears a category.";
  }
  return "Motzu sets this category when nothing else matched, so Shopify never guesses one from the title. Pick Don't set to leave Shopify's category as it is.";
}

/** True when the store chose "Don't set" and the flag lets it count; the server ignores it otherwise. */
export function unmappedCategoryIsUnset(layout: StoreLayoutSettings | undefined, flagOn: boolean): boolean {
  return flagOn && layout?.unmappedTaxonomyCategory === UNMAPPED_CATEGORY_UNSET;
}

export function taxonomyLabel(options: Array<{ gid: string; label: string }>, gid: string | null | undefined): string {
  if (!gid) return '';
  return options.find((option) => option.gid === gid)?.label || gid.replace(/^gid:\/\/shopify\/TaxonomyCategory\//, '');
}

/**
 * Shopify standard category a Motzu category slug syncs to: the store's own
 * row, else Motzu's built-in default, else the store's fallback, else the
 * neutral top level. Same order as the server's `resolveCategoryGid`, minus
 * its text inference, which only runs for a product that names no category.
 * With `unmappedUnset` (flag `shopify_unmapped_category_unset` on) and the store's
 * fallback set to "Don't set", the last step is `{ gid: '', step: 'unset' }`: no
 * category is sent, so the listing keeps its own. Flag off, a stored "Don't set"
 * reads as no fallback and the neutral top level applies, as the server does.
 */
export function resolveTaxonomyForSlug(
  slug: string,
  layout: StoreLayoutSettings | undefined,
  defaults: { defaults: Record<string, string | null>; neutralGid: string } | undefined,
  options?: { unmappedUnset?: boolean },
): { gid: string; step: 'store' | 'default' | 'fallback' | 'unset' | 'neutral' } | null {
  const own = layout?.taxonomyCategoryMap?.[slug];
  if (own) return { gid: own, step: 'store' };
  const builtIn = defaults?.defaults[slug];
  if (builtIn) return { gid: builtIn, step: 'default' };
  const fallback = unmappedFallbackGid(layout);
  if (fallback) return { gid: fallback, step: 'fallback' };
  if (unmappedCategoryIsUnset(layout, options?.unmappedUnset === true)) return { gid: '', step: 'unset' };
  return defaults ? { gid: defaults.neutralGid, step: 'neutral' } : null;
}

/** "Use defaults": drops every taxonomy choice and the fallback, so Motzu's built-in map decides. */
export function withTaxonomyDefaults(layout: StoreLayoutSettings): StoreLayoutSettings {
  const { taxonomyCategoryMap: _map, unmappedTaxonomyCategory: _fallback, ...rest } = layout;
  return rest;
}

export function usesTaxonomyDefaults(layout: StoreLayoutSettings | undefined): boolean {
  return Object.keys(layout?.taxonomyCategoryMap || {}).length === 0 && !layout?.unmappedTaxonomyCategory;
}
